import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseLineupText, type LineupImportRow } from '@pirouette/shared';
import { useProduction, useDances, useShows, useCreateDance } from '@/data/productions';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import { ImportSummaryView } from '@/components/import/ImportSummaryView';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/productions/$id/import-lineup')({
  component: LineupImportPage,
});

function LineupImportPage() {
  const { id: productionId } = Route.useParams();
  const navigate = useNavigate();
  const production = useProduction(productionId);
  const dances = useDances(productionId);
  const shows = useShows(productionId);
  const createDance = useCreateDance();
  const qc = useQueryClient();

  const [text, setText] = useState('');
  const [preview, setPreview] = useState<ReturnType<typeof parseLineupText> | null>(null);
  const [selectedShowId, setSelectedShowId] = useState<string>('');

  const commit = useMutation({
    mutationFn: async (rows: LineupImportRow[]) => {
      if (!selectedShowId) throw new Error('Pick a show first');
      // For each row: ensure a dance exists with that name (create if missing),
      // then upsert lineup row at the given sequence. Cast names are resolved
      // best-effort against existing dancers by full name.
      const existingByName = new Map(
        (dances.data ?? []).map((d) => [d.name.toLowerCase(), d.id] as const),
      );
      const danceIdBySeq = new Map<number, string>();

      for (const row of rows) {
        let danceId = existingByName.get(row.dance_name.toLowerCase());
        if (!danceId) {
          const created = await createDance.mutateAsync({
            production_id: productionId,
            name: row.dance_name,
            style: row.style ?? 'other',
            length_seconds: row.length_seconds,
            notes: row.notes ?? undefined,
          });
          danceId = created.id;
          existingByName.set(row.dance_name.toLowerCase(), danceId);
        }
        danceIdBySeq.set(row.sequence, danceId);
      }

      // Bulk-upsert lineup rows.
      const lineupRows = [...danceIdBySeq.entries()].map(([seq, danceId]) => ({
        show_id: selectedShowId,
        dance_id: danceId,
        sequence_index: seq,
      }));
      const { error: e1 } = await supabase
        .from('show_lineups')
        .upsert(lineupRows, { onConflict: 'show_id,dance_id' });
      if (e1) throw e1;

      // Resolve cast best-effort.
      const allCastNames = new Set<string>();
      for (const row of rows) for (const n of row.cast_names) allCastNames.add(n);
      if (allCastNames.size > 0) {
        const { data: matched } = await supabase
          .from('dancers')
          .select('id, first_name, last_name, preferred_name')
          .is('deleted_at', null);
        const lookup = new Map<string, string>();
        const ambiguousNames = new Set<string>();
        for (const d of (matched ?? []) as {
          id: string;
          first_name: string;
          last_name: string;
          preferred_name: string | null;
        }[]) {
          const primary = `${d.first_name} ${d.last_name}`.toLowerCase();
          const preferred = d.preferred_name
            ? `${d.preferred_name} ${d.last_name}`.toLowerCase()
            : null;
          for (const key of preferred ? [primary, preferred] : [primary]) {
            if (lookup.has(key) && lookup.get(key) !== d.id) {
              // Two dancers share a name — silently dropping one (the
              // previous code's behavior) cast the wrong child. Surface
              // the ambiguity instead.
              ambiguousNames.add(key);
              lookup.delete(key);
            } else {
              lookup.set(key, d.id);
            }
          }
        }
        if (ambiguousNames.size > 0) {
          toast({
            title: `${ambiguousNames.size} ambiguous cast ${ambiguousNames.size === 1 ? 'name' : 'names'}`,
            description: `Some names match more than one dancer. Cast them by hand in the lineup builder.`,
            variant: 'error',
          });
        }
        const castRows: { dance_id: string; dancer_id: string }[] = [];
        for (const row of rows) {
          const danceId = danceIdBySeq.get(row.sequence);
          if (!danceId) continue;
          for (const name of row.cast_names) {
            const id = lookup.get(name.toLowerCase());
            if (id) castRows.push({ dance_id: danceId, dancer_id: id });
          }
        }
        if (castRows.length > 0) {
          const { error: e2 } = await supabase
            .from('cast_assignments')
            .upsert(castRows, { onConflict: 'dance_id,dancer_id' });
          if (e2) throw e2;
        }
      }

      const { error: rpcErr } = await supabase.rpc('recompute_show_timeline', {
        p_show_id: selectedShowId,
      });
      if (rpcErr) throw rpcErr;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lineup'] });
      void qc.invalidateQueries({ queryKey: ['dances', productionId] });
      void qc.invalidateQueries({ queryKey: ['cast-for-show'] });
    },
  });

  function handleParse() {
    const result = parseLineupText(text);
    setPreview(result);
  }

  async function handleCommit() {
    if (!preview) return;
    try {
      await commit.mutateAsync(preview.rows);
      toast({
        title: 'Lineup imported',
        description: `${preview.rows.length} dances loaded into the show.`,
        variant: 'success',
      });
      await navigate({
        to: '/admin/productions/$id/shows/$showId',
        params: { id: productionId, showId: selectedShowId },
      });
    } catch (err) {
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="max-w-4xl space-y-8">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← {production.data?.name ?? 'Production'}
        </Link>
        <h1 className="mt-2">Import lineup</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Paste your studio spreadsheet (pipe or comma delimited). We'll create any missing dances
          and best-effort-resolve cast names against the existing dancer roster.
        </p>
      </header>

      {shows.data?.length === 0 && (
        <Empty
          title="No shows in this production yet"
          description="Add a show first — the lineup importer attaches dances to one specific show."
          action={
            <Link to="/admin/productions/$id" params={{ id: productionId }}>
              <Button>Back to production</Button>
            </Link>
          }
        />
      )}

      {shows.data && shows.data.length > 0 && (
        <section className="surface-elevated space-y-4 p-5">
          <div>
            <label htmlFor="show-pick" className="block font-medium text-[var(--text-sm)]">
              Target show
            </label>
            <select
              id="show-pick"
              className="mt-2 h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={selectedShowId}
              onChange={(e) => setSelectedShowId(e.target.value)}
            >
              <option value="" disabled>
                Pick a show…
              </option>
              {shows.data.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({new Date(s.performance_at).toLocaleString()})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="lineup-csv" className="block font-medium text-[var(--text-sm)]">
              Lineup text
            </label>
            <textarea
              id="lineup-csv"
              rows={10}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                'sequence|dance_name|style|length_seconds|cast\n' +
                '1|Opening Number|jazz|180|Alex Smith; Ash Smith\n' +
                '2|Pas de Deux|ballet|240|Alex Smith\n'
              }
              className="mt-2 w-full resize-y rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 font-[var(--font-mono)] text-[var(--text-sm)] focus-visible:outline-none"
            />
          </div>

          <Button onClick={handleParse} disabled={!text.trim()}>
            Preview
          </Button>
        </section>
      )}

      {preview && (
        <section aria-labelledby="lineup-preview-heading" className="space-y-4">
          <h2 id="lineup-preview-heading" className="font-medium text-[var(--text-lg)]">
            Preview
          </h2>
          <ImportSummaryView summary={preview.summary} />
          <Button
            onClick={() => void handleCommit()}
            disabled={!selectedShowId || preview.rows.length === 0 || commit.isPending}
          >
            {commit.isPending ? 'Importing…' : `Commit ${preview.rows.length} dances`}
          </Button>
        </section>
      )}
    </div>
  );
}
